import * as DocumentPicker from "expo-document-picker";
import { useLocalSearchParams, useRouter } from "expo-router";
import { CheckCircle2, Circle, Clock, FileText, HeartHandshake, PenLine, UploadCloud } from "lucide-react-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";

import { PageShell, SectionIntro } from "../../../src/components/layout";
import { Reveal } from "../../../src/components/reveal";
import { Badge, Button, EmptyState, ErrorState, LoadingState, Panel, SegmentedPill } from "../../../src/components/ui";
import { FamilyPortalMemorialTab } from "../../../src/components/family-portal/FamilyPortalMemorialTab";
import {
  createFamilyPortalIdempotencyKey,
  describeFamilyPortalOutboundState,
  familyPortalSigningPath,
  finalizeFamilyPortalUpload,
  getFamilyPortalErrorMessage,
  getPublicFamilyPortal,
  reserveFamilyPortalUpload,
  retryFamilyPortalUploadScan,
  sendPublicFamilyPortalMessage,
  type FamilyPortalOutboundState as PortalOutboundState,
  type FamilyPortalPacketSummary,
  type FamilyPortalProjection,
  type FamilyPortalUpload,
} from "../../../src/lib/central-family-portal";
import { palette, radii, spacing } from "../../../src/theme/tokens";

const PORTAL_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,120}$/;

type PortalTab = "status" | "documents" | "sign" | "messages" | "memorial";

const PORTAL_TABS: Array<{ id: PortalTab; label: string }> = [
  { id: "status", label: "Status" },
  { id: "documents", label: "Documents" },
  { id: "sign", label: "Sign" },
  { id: "messages", label: "Messages" },
  { id: "memorial", label: "Memorial" },
];

/**
 * The memorial tab is gated on the projection instead of always shown.
 * `memorialTabAvailable` stays false until WP-2.3 backs the tab with portal
 * contract data, and a permanently visible tab whose only possible content is
 * "comes later" is worse for a grieving family than no tab at all.
 */
function visiblePortalTabs(
  projection: FamilyPortalProjection,
): Array<{ id: PortalTab; label: string }> {
  return PORTAL_TABS.filter(
    (tab) => tab.id !== "memorial" || projection.memorialTabAvailable,
  );
}

const UPLOAD_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "identification", label: "Identification" },
  { id: "death_certificates", label: "Death certificates" },
  { id: "permits", label: "Permits" },
  { id: "authorizations", label: "Authorizations" },
  { id: "veteran_documents", label: "Veteran documents" },
  { id: "financial", label: "Financial" },
  { id: "insurance", label: "Insurance" },
  { id: "contracts", label: "Contracts" },
  { id: "service_planning", label: "Service planning" },
  { id: "cemetery", label: "Cemetery" },
  { id: "crematory", label: "Crematory" },
  { id: "other", label: "Other" },
];

export default function FamilyPortalScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const portalToken = Array.isArray(token) ? token[0] ?? "" : token ?? "";

  const [projection, setProjection] = useState<FamilyPortalProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState<PortalTab>("status");

  const load = useCallback(async () => {
    if (!PORTAL_TOKEN_PATTERN.test(portalToken)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      setProjection(await getPublicFamilyPortal(portalToken));
    } catch (caught) {
      setLoadError(getFamilyPortalErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [portalToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!PORTAL_TOKEN_PATTERN.test(portalToken)) {
    return (
      <PageShell>
        <SectionIntro
          eyebrow="Family Portal"
          title="This link is not valid."
          description="The link may have expired or is incorrect. Please contact the funeral home for a new link."
        />
      </PageShell>
    );
  }

  if (loading) {
    return (
      <PageShell role="family">
        <LoadingState
          label="Opening your portal"
          description="One moment while we prepare your private space."
        />
      </PageShell>
    );
  }

  if (!projection || loadError) {
    return (
      <PageShell>
        <SectionIntro
          eyebrow="Family Portal"
          title="This link is not available."
          description={
            loadError ||
            "The link may have expired or been replaced. Please contact your care team for a new link."
          }
        />
      </PageShell>
    );
  }

  // Completed cases stay viewable but stop accepting documents and messages
  // (O2). The server enforces this too; the timeline carries the truth.
  const readOnly = projection.timeline.every(
    (entry) => entry.state === "complete",
  );

  const tabs = visiblePortalTabs(projection);
  // A reload can withdraw the selected tab; never render an unavailable one.
  const currentTab: PortalTab = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : "status";

  return (
    <PageShell role="family">
      <View style={styles.content}>
        <Reveal>
          <View style={styles.welcomeHeader}>
            <Text style={styles.welcomeEyebrow}>Family Portal</Text>
            <Text style={styles.welcomeTitle}>{projection.lovedOneName}</Text>
            <Text style={styles.welcomeSubtitle}>
              Your private space with the {projection.funeralHomeName} care team.
            </Text>
          </View>
        </Reveal>

        <ScrollView
          contentContainerStyle={styles.tabScroll}
          horizontal
          showsHorizontalScrollIndicator={false}
        >
          <View accessibilityRole="tablist" style={styles.tabRow}>
            {tabs.map((tab) => (
              <View
                accessibilityRole="tab"
                accessibilityState={{ selected: currentTab === tab.id }}
                key={tab.id}
              >
                <SegmentedPill
                  active={currentTab === tab.id}
                  label={tab.label}
                  selectionRole="radio"
                  onPress={() => setActiveTab(tab.id)}
                />
              </View>
            ))}
          </View>
        </ScrollView>

        {currentTab === "status" ? (
          <StatusTab projection={projection} readOnly={readOnly} />
        ) : currentTab === "documents" ? (
          <DocumentsTab
            projection={projection}
            readOnly={readOnly}
            token={portalToken}
            onChanged={() => void load()}
          />
        ) : currentTab === "sign" ? (
          <SignTab projection={projection} />
        ) : currentTab === "messages" ? (
          <MessagesTab
            projection={projection}
            readOnly={readOnly}
            token={portalToken}
            onChanged={() => void load()}
          />
        ) : currentTab === "memorial" ? (
          <FamilyPortalMemorialTab memorialSlug={projection.memorialSlug} />
        ) : null}
      </View>
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/* Status tab                                                          */
/* ------------------------------------------------------------------ */

function StatusTab({
  projection,
  readOnly,
}: {
  projection: FamilyPortalProjection;
  readOnly: boolean;
}) {
  const linkExpiresLabel = useMemo(() => {
    const parsed = Date.parse(projection.linkExpiresAt);
    if (Number.isNaN(parsed)) return "";
    return new Date(parsed).toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [projection.linkExpiresAt]);

  return (
    <View style={styles.tabContent}>
      <Reveal>
        <Panel>
          <Text style={styles.panelTitle}>Where things stand</Text>
          <View style={styles.timeline}>
            {projection.timeline.map((entry) => (
              <View key={entry.id} style={styles.timelineRow}>
                {entry.state === "complete" ? (
                  <CheckCircle2 color={palette.forest} size={20} strokeWidth={1.8} />
                ) : entry.state === "current" ? (
                  <Clock color={palette.cedar} size={20} strokeWidth={1.8} />
                ) : (
                  <Circle color={palette.mist} size={20} strokeWidth={1.8} />
                )}
                <View style={styles.timelineCopy}>
                  <Text
                    style={[
                      styles.timelineLabel,
                      entry.state === "current" ? styles.timelineCurrent : null,
                      entry.state === "upcoming" ? styles.timelineUpcoming : null,
                    ]}
                  >
                    {entry.label}
                  </Text>
                  {entry.state === "current" ? (
                    <Text style={styles.timelineState}>Happening now</Text>
                  ) : entry.state === "complete" ? (
                    <Text style={styles.timelineState}>Complete</Text>
                  ) : (
                    <Text style={styles.timelineState}>Coming up</Text>
                  )}
                </View>
              </View>
            ))}
          </View>
          {readOnly ? (
            <Text style={styles.readOnlyNote}>
              This case is complete. Everything shared here stays available to
              you, and your care team remains just a phone call away.
            </Text>
          ) : null}
        </Panel>
      </Reveal>

      <Reveal delay={80}>
        <Panel>
          <Text style={styles.panelTitle}>What we need from you</Text>
          {projection.missingItems.length ? (
            <View style={styles.missingList}>
              {projection.missingItems.map((item) => (
                <View key={item.id} style={styles.missingRow}>
                  <FileText color={palette.cedar} size={17} strokeWidth={1.8} />
                  <View style={styles.missingCopy}>
                    <Text style={styles.missingTitle}>{item.title}</Text>
                    {item.requestedAt && Date.parse(item.requestedAt) ? (
                      <Text style={styles.missingRequested}>
                        Requested{" "}
                        {new Date(item.requestedAt).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "long",
                        })}
                      </Text>
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.muted}>
              Nothing is needed from you right now. We will update this list as
              things change.
            </Text>
          )}
        </Panel>
      </Reveal>

      <Reveal delay={140}>
        <Panel style={styles.carePanel}>
          <View style={styles.careRow}>
            <HeartHandshake color={palette.forest} size={18} strokeWidth={1.8} />
            <Text style={styles.careText}>
              Your care team speaks with one voice here. If anything is unclear,
              use the Messages tab or call your funeral home directly.
            </Text>
          </View>
          {linkExpiresLabel ? (
            <Text style={styles.muted}>
              This private link stays available until {linkExpiresLabel}. Your
              care team can send a fresh one at any time.
            </Text>
          ) : null}
        </Panel>
      </Reveal>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* Documents tab                                                       */
/* ------------------------------------------------------------------ */

function DocumentsTab({
  projection,
  readOnly,
  token,
  onChanged,
}: {
  projection: FamilyPortalProjection;
  readOnly: boolean;
  token: string;
  onChanged: () => void;
}) {
  const [category, setCategory] = useState(
    UPLOAD_CATEGORIES[0]?.id ?? "identification",
  );
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadNotice, setUploadNotice] = useState("");

  async function pickAndUpload() {
    setBusy(true);
    setUploadError("");
    setUploadNotice("");
    try {
      const picked = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        type: ["application/pdf", "image/jpeg", "image/png"],
      });
      const asset = picked.canceled ? null : picked.assets[0];
      if (!asset) {
        setBusy(false);
        return;
      }
      const blobResponse = await fetch(asset.uri);
      const blob = await blobResponse.blob();
      const sizeBytes = asset.size ?? blob.size;
      const declaredMimeType =
        asset.mimeType ?? blob.type ?? "application/octet-stream";
      const reservation = await reserveFamilyPortalUpload({
        category,
        declaredMimeType,
        fileName: asset.name,
        idempotencyKey: createFamilyPortalIdempotencyKey("upload"),
        sizeBytes,
        title: title.trim() || asset.name,
        token,
      });
      await fetch(reservation.upload.url, {
        body: blob,
        headers: { "Content-Type": reservation.upload.contentType },
        method: "PUT",
      });
      const finalized = await finalizeFamilyPortalUpload({
        quarantineId: reservation.quarantine.quarantineId,
        token,
      });
      setUploadNotice(finalized.quarantine.supportMessage);
      setTitle("");
      onChanged();
    } catch (caught) {
      setUploadError(getFamilyPortalErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function retryScan(upload: FamilyPortalUpload) {
    setBusy(true);
    setUploadError("");
    try {
      const retried = await retryFamilyPortalUploadScan({
        expectedRevision: upload.revision,
        quarantineId: upload.quarantineId,
        token,
      });
      setUploadNotice(retried.quarantine.supportMessage);
      onChanged();
    } catch (caught) {
      setUploadError(getFamilyPortalErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <View style={styles.tabContent}>
      <Reveal>
        <Panel>
          <Text style={styles.panelTitle}>Documents you have shared</Text>
          {projection.uploads.length ? (
            <View style={styles.uploadList}>
              {projection.uploads.map((upload) => (
                <View key={upload.quarantineId} style={styles.uploadRow}>
                  <View style={styles.uploadCopy}>
                    <Text style={styles.uploadTitle}>{upload.title}</Text>
                    <Text style={styles.muted}>{upload.supportMessage}</Text>
                  </View>
                  <View style={styles.uploadActions}>
                    <Badge
                      label={uploadStatusLabel(upload.status)}
                      tone={
                        upload.status === "clean"
                          ? "forest"
                          : upload.status === "quarantined" ||
                              upload.status === "scan_failed"
                            ? "gold"
                            : "mist"
                      }
                    />
                    {upload.status === "scan_failed" && !readOnly ? (
                      <Button
                        compact
                        disabled={busy}
                        label="Retry safety check"
                        variant="secondary"
                        onPress={() => void retryScan(upload)}
                      />
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.muted}>
              No documents yet. When your care team asks for one, you can add it
              here.
            </Text>
          )}
        </Panel>
      </Reveal>

      {readOnly ? (
        <Reveal delay={80}>
          <Panel>
            <Text style={styles.panelTitle}>Add a document</Text>
            <Text style={styles.muted}>
              This case is complete, so the portal can no longer accept
              documents. If your care team needs anything else, they will help
              you directly.
            </Text>
          </Panel>
        </Reveal>
      ) : (
        <Reveal delay={80}>
          <Panel>
            <View style={styles.uploadFormHeader}>
              <UploadCloud color={palette.cedar} size={18} strokeWidth={1.8} />
              <Text style={styles.panelTitle}>Add a document</Text>
            </View>
            <Text style={styles.muted}>
              PDF, JPEG, or PNG files up to 10 MB. Every file is safely held for
              a security check before anyone can see it.
            </Text>
            <View style={styles.categoryRow}>
              {UPLOAD_CATEGORIES.slice(0, 6).map((choice) => (
                <SegmentedPill
                  active={category === choice.id}
                  key={choice.id}
                  label={choice.label}
                  selectionRole="radio"
                  onPress={() => setCategory(choice.id)}
                />
              ))}
            </View>
            <Text style={styles.fieldLabel}>Document title</Text>
            <TextInput
              accessibilityLabel="Document title"
              onChangeText={setTitle}
              placeholder="For example: Insurance letter"
              placeholderTextColor={palette.inkSoft}
              style={styles.textInput}
              value={title}
            />
            {uploadError ? (
              <ErrorState title="Upload not completed" description={uploadError} />
            ) : null}
            {uploadNotice ? (
              <Text accessibilityLiveRegion="polite" style={styles.notice}>
                {uploadNotice}
              </Text>
            ) : null}
            <Button
              compact
              disabled={busy}
              label={busy ? "Uploading" : "Choose file and upload"}
              loading={busy}
              onPress={() => void pickAndUpload()}
            />
          </Panel>
        </Reveal>
      )}
    </View>
  );
}

function uploadStatusLabel(status: FamilyPortalUpload["status"]) {
  switch (status) {
    case "clean":
      return "Safety check complete";
    case "quarantined":
      return "Not shared";
    case "scan_failed":
      return "Check unavailable";
    case "expired":
      return "Expired";
    case "reserved":
      return "Upload in progress";
    default:
      return "Held for safety check";
  }
}

/* ------------------------------------------------------------------ */
/* Sign tab                                                            */
/* ------------------------------------------------------------------ */

function SignTab({ projection }: { projection: FamilyPortalProjection }) {
  const router = useRouter();
  return (
    <View style={styles.tabContent}>
      {projection.signaturePackets.length ? (
        projection.signaturePackets.map((packet, index) => (
          <Reveal delay={index * 60} key={packet.packetId}>
            <PacketCard
              onSign={(path) => router.push(path as never)}
              packet={packet}
            />
          </Reveal>
        ))
      ) : (
        <Reveal>
          <Panel>
            <EmptyState
              title="Nothing to sign right now"
              description="When your care team prepares paperwork for you, it will appear here."
            />
          </Panel>
        </Reveal>
      )}
    </View>
  );
}

function PacketCard({
  onSign,
  packet,
}: {
  onSign: (path: string) => void;
  packet: FamilyPortalPacketSummary;
}) {
  const signingPath = familyPortalSigningPath(packet);
  return (
    <Panel style={styles.packetCard}>
      <View style={styles.packetHeader}>
        <PenLine color={palette.cedar} size={18} strokeWidth={1.8} />
        <Text style={styles.packetTitle}>{packet.title}</Text>
      </View>
      {packet.state === "needs_signature" ? (
        signingPath ? (
          <>
            <Text style={styles.muted}>
              Your care team is waiting for your signature on this packet.
            </Text>
            <Button
              compact
              label="Review and sign"
              onPress={() => onSign(signingPath)}
            />
          </>
        ) : (
          <Text style={styles.muted}>
            Signing is being prepared. Refresh in a few minutes.
          </Text>
        )
      ) : packet.state === "signed" ? (
        <View style={styles.packetStatusRow}>
          <Badge label="Signed" tone="forest" />
          <Text style={styles.muted}>
            Thank you. Your care team is finishing the remaining steps.
          </Text>
        </View>
      ) : packet.state === "completed" ? (
        <View style={styles.packetStatusRow}>
          <Badge label="Completed" tone="forest" />
          <Text style={styles.muted}>This packet is complete.</Text>
        </View>
      ) : (
        <View style={styles.packetStatusRow}>
          <Badge label="No longer needed" tone="mist" />
          <Text style={styles.muted}>
            This packet was withdrawn. Your care team can explain why.
          </Text>
        </View>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Messages tab                                                        */
/* ------------------------------------------------------------------ */

function MessagesTab({
  projection,
  readOnly,
  token,
  onChanged,
}: {
  projection: FamilyPortalProjection;
  readOnly: boolean;
  token: string;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [outbound, setOutbound] = useState<PortalOutboundState | null>(null);
  const [messageError, setMessageError] = useState("");

  const isOffline =
    Platform.OS === "web" &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false;

  async function send() {
    const body = draft.trim();
    if (body.length < 2) return;
    if (isOffline) {
      setOutbound("queued_on_device");
      return;
    }
    setOutbound("sending");
    setMessageError("");
    try {
      await sendPublicFamilyPortalMessage({
        idempotencyKey: createFamilyPortalIdempotencyKey("message"),
        message: body,
        token,
      });
      setOutbound("sent");
      setDraft("");
      onChanged();
    } catch (caught) {
      setOutbound("failed");
      setMessageError(getFamilyPortalErrorMessage(caught));
    }
  }

  return (
    <View style={styles.tabContent}>
      <Reveal>
        <Panel>
          <Text style={styles.panelTitle}>Messages with your care team</Text>
          {projection.messages.length ? (
            <ScrollView contentContainerStyle={styles.thread}>
              {projection.messages.map((message) => (
                <View
                  key={message.id}
                  style={[
                    styles.bubble,
                    message.from === "family"
                      ? styles.bubbleFamily
                      : styles.bubbleCareTeam,
                  ]}
                >
                  <Text style={styles.bubbleFrom}>
                    {message.from === "family" ? "You" : "Care team"}
                  </Text>
                  <Text style={styles.bubbleBody}>{message.body}</Text>
                  <Text style={styles.bubbleTime}>
                    {new Date(message.sentAt).toLocaleString(undefined, {
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      month: "short",
                    })}
                  </Text>
                </View>
              ))}
            </ScrollView>
          ) : (
            <Text style={styles.muted}>
              No messages yet. Write below and your care team will see it in
              this portal.
            </Text>
          )}
        </Panel>
      </Reveal>

      <Reveal delay={80}>
        <Panel>
          {readOnly ? (
            <Text style={styles.muted}>
              This case is complete, so the portal can no longer accept
              messages. If you need anything, please call your funeral home —
              they are glad to help.
            </Text>
          ) : (
            <>
              <Text style={styles.fieldLabel}>Write to your care team</Text>
              <TextInput
                accessibilityLabel="Message to your care team"
                maxLength={2000}
                multiline
                numberOfLines={4}
                onChangeText={(value) => {
                  setDraft(value);
                  if (outbound) setOutbound(null);
                }}
                placeholder="Share an update or ask a question…"
                placeholderTextColor={palette.inkSoft}
                style={[styles.textInput, styles.messageInput]}
                value={draft}
              />
              {outbound ? (
                <Text
                  accessibilityLiveRegion="polite"
                  style={styles.notice}
                >
                  {describeFamilyPortalOutboundState(outbound)}
                </Text>
              ) : null}
              {messageError ? (
                <ErrorState title="Message not sent" description={messageError} />
              ) : null}
              <Button
                compact
                disabled={draft.trim().length < 2 || outbound === "sending"}
                label="Send"
                loading={outbound === "sending"}
                onPress={() => void send()}
              />
            </>
          )}
        </Panel>
      </Reveal>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = StyleSheet.create({
  content: {
    gap: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  welcomeHeader: {
    gap: 6,
  },
  welcomeEyebrow: {
    color: palette.cedar,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  welcomeTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 38,
    lineHeight: 42,
  },
  welcomeSubtitle: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 15,
    lineHeight: 24,
  },
  tabScroll: {
    gap: 8,
    paddingBottom: 4,
  },
  tabRow: {
    flexDirection: "row",
    gap: 8,
  },
  tabContent: {
    gap: spacing.lg,
  },
  panelTitle: {
    color: palette.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 16,
    marginBottom: 10,
  },
  timeline: {
    gap: 12,
  },
  timelineRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
  },
  timelineCopy: {
    flex: 1,
  },
  timelineLabel: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
  },
  timelineCurrent: {
    color: palette.cedar,
  },
  timelineUpcoming: {
    color: palette.inkSoft,
  },
  timelineState: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
  },
  readOnlyNote: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
    marginTop: 12,
  },
  missingList: {
    gap: 12,
  },
  missingRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
  },
  missingCopy: {
    flex: 1,
  },
  missingTitle: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
  },
  missingRequested: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 12,
  },
  muted: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
  },
  carePanel: {
    gap: 8,
  },
  careRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
  },
  careText: {
    color: palette.ink,
    flex: 1,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
  },
  uploadList: {
    gap: 14,
  },
  uploadRow: {
    gap: 8,
  },
  uploadCopy: {
    gap: 2,
  },
  uploadTitle: {
    color: palette.ink,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 14,
  },
  uploadActions: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: 8,
  },
  uploadFormHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
  },
  categoryRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginVertical: 10,
  },
  fieldLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 10,
    textTransform: "uppercase",
  },
  textInput: {
    backgroundColor: palette.white,
    borderColor: palette.line,
    borderRadius: radii.sm,
    borderWidth: 1,
    color: palette.ink,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 12,
    padding: 12,
  },
  messageInput: {
    minHeight: 90,
  },
  notice: {
    color: palette.forest,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 10,
  },
  packetCard: {
    gap: 10,
  },
  packetHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
  },
  packetTitle: {
    color: palette.ink,
    flex: 1,
    fontFamily: "Manrope_700Bold",
    fontSize: 15,
  },
  packetStatusRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
  },
  thread: {
    gap: 10,
  },
  bubble: {
    borderRadius: radii.sm,
    gap: 3,
    maxWidth: "92%",
    padding: 12,
  },
  bubbleFamily: {
    alignSelf: "flex-end",
    backgroundColor: palette.canvasAlt,
  },
  bubbleCareTeam: {
    alignSelf: "flex-start",
    backgroundColor: palette.white,
    borderColor: palette.mist,
    borderWidth: 1,
  },
  bubbleFrom: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  bubbleBody: {
    color: palette.ink,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 21,
  },
  bubbleTime: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 11,
  },
});
